import { Badge, Search } from '@shinetools/lumen-react'
import { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'

import { useSegment } from '../../../../../../hooks'
import { defaultTransactionsQuery } from '../../../../constants/defaultTransactionsQuery'
import { useTransactionsListFilters } from '../../../../contexts/transactionsListFiltersContext'
import { TransactionsSearchFilterMenu } from './elements/TransactionsSearchFilterMenu'
import { useTransactionsActiveFiltersCount } from './hooks/useTransactionsActiveFiltersCount'
import * as Styled from './styles'

const SEARCH_DEBOUNCE_MS = 250

export const TransactionsSearch = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const {
    appliedFilters: { searchQuery },
    setFilters,
  } = useTransactionsListFilters()

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(searchQuery ?? '')

  const activeFiltersCount = useTransactionsActiveFiltersCount()

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }, [])

  const handleFilterClick = useCallback(() => {
    setIsFilterMenuOpen(true)
  }, [])

  useDebounce(
    () => {
      if (searchValue === (searchQuery ?? '')) {
        return
      }

      setFilters({ page: defaultTransactionsQuery.page, searchQuery: searchValue || undefined })

      if (!searchValue) {
        return
      }

      track('xxx - Bookkeeping - Ledger', { action: 'Search', keyword: searchValue })
    },
    SEARCH_DEBOUNCE_MS,
    [searchValue],
  )

  /* Picks up search query from URL */
  useEffect(() => {
    setSearchValue(searchQuery ?? '')
  }, [searchQuery])

  return (
    <Styled.TransactionsSearchWrapper>
      <TransactionsSearchFilterMenu isOpen={isFilterMenuOpen} onOpenChange={setIsFilterMenuOpen} />
      <Search
        filterIcon={
          activeFiltersCount > 0 ? <Badge color="accent" count={activeFiltersCount} size="medium" /> : undefined
        }
        rounded
        noDropdown
        onChange={handleSearchChange}
        onFilterClick={handleFilterClick}
        placeholder={`${t('search')}...`}
        size="small"
        value={searchValue}
      />
    </Styled.TransactionsSearchWrapper>
  )
}
